import { DeleteOutlined } from '@ant-design/icons'
import { Button, Checkbox, DatePicker, Flex, Radio, Select, Space, Table } from 'antd'
import { ColumnsType } from 'antd/es/table'
import Title from 'antd/es/typography/Title'
import { Dayjs } from 'dayjs'
import { FC } from 'react'
import { outputDateFormat } from '../../../../../../constants/constants'
import { useGetContractorsQuery } from '../../../../../../store/api/contractors-api'
import Form from '../../../../../../types/api/form'
import { QueryParams } from '../../../../../../types/strapi/strapi'
import { draftNumPreview, NumberingMode } from '../../types/order-draft'
import ColorSwatch from '../color-swatch'

const supplierParams: QueryParams = {
    filters: { type: { name: { $contains: 'Поставщик форм' } } },
}

interface FormsOrderEditorProps {
    orderId: number
    numberingMode: NumberingMode
    draftForms: Form[]
    keepNumByFormId: Record<string, boolean>
    supplierDocId?: string
    estimatedDate: Dayjs | null
    assignedNum?: string // YY-set-order после save; до save preview — плейсхолдер
    readOnly?: boolean // заказ уже сохранён: состав и нумерацию не меняем
    onNumberingModeChange: (mode: NumberingMode) => void
    onToggleKeepNum: (formDocumentId: string, keepNum: boolean) => void
    onRemoveFromDraft: (formDocumentId: string) => void
    onSupplierChange: (docId: string | undefined) => void
    onEstimatedDateChange: (date: Dayjs | null) => void
}

export const FormsOrderEditor: FC<FormsOrderEditorProps> = ({
    orderId,
    numberingMode,
    draftForms,
    keepNumByFormId,
    supplierDocId,
    estimatedDate,
    assignedNum,
    readOnly,
    onNumberingModeChange,
    onToggleKeepNum,
    onRemoveFromDraft,
    onSupplierChange,
    onEstimatedDateChange,
}) => {
    const { data: contractors, isFetching } = useGetContractorsQuery(supplierParams)

    const columns: ColumnsType<Form> = [
        {
            title: 'Номер формы',
            key: 'num',
            width: 310,
            render: (_, form) => (
                <Flex justify='space-between' align='center' gap={8}>
                    <span>
                        {draftNumPreview(
                            numberingMode,
                            !!keepNumByFormId[form.documentId],
                            form,
                            assignedNum
                        )}
                    </span>
                    {numberingMode === 'combined' && !readOnly ? (
                        <Checkbox
                            style={{ whiteSpace: 'nowrap' }}
                            checked={!!keepNumByFormId[form.documentId]}
                            onChange={e => onToggleKeepNum(form.documentId, e.target.checked)}
                        >
                            Сохранить номер
                        </Checkbox>
                    ) : null}
                </Flex>
            ),
        },
        {
            title: 'Цвет',
            key: 'color',
            width: 80,
            render: (_, form) => (form.color ? <ColorSwatch $hex={form.color} /> : null),
        },
        {
            title: 'Название',
            key: 'colorName',
            render: (_, form) => form.colorName,
        },
    ]

    if (!readOnly) {
        columns.push({
            title: '',
            key: 'actions',
            width: 80,
            render: (_, form) => (
                <Button
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => onRemoveFromDraft(form.documentId)}
                />
            ),
        })
    }

    return (
        <Space direction='vertical' size={16} style={{ width: '100%' }}>
            <Flex justify='space-between' align='flex-end' wrap='wrap' gap={16}>
                <div>
                    <Title level={3} style={{ marginTop: 0 }}>
                        {readOnly ? `Заказ форм № ${orderId}` : 'Черновик заказа'}
                    </Title>
                    <Radio.Group
                        value={numberingMode}
                        onChange={e => onNumberingModeChange(e.target.value)}
                        optionType='button'
                        buttonStyle='solid'
                        disabled={readOnly}
                    >
                        <Radio.Button value='new'>Новые номера</Radio.Button>
                        <Radio.Button value='keep'>Старые номера</Radio.Button>
                        {/* <Radio.Button value='combined'>Комбинированные</Radio.Button> */}
                    </Radio.Group>
                </div>
                <Flex gap={16} align='flex-end' wrap='wrap'>
                    <div style={{ minWidth: 220 }}>
                        <div style={{ marginBottom: 4 }}>Поставщик</div>
                        <Select
                            allowClear
                            showSearch
                            optionFilterProp='label'
                            placeholder='Поставщик'
                            style={{ width: '100%' }}
                            loading={isFetching}
                            disabled={readOnly}
                            value={supplierDocId}
                            onChange={onSupplierChange}
                            options={contractors?.data.map(x => ({
                                label: x.name,
                                value: x.documentId,
                            }))}
                        />
                    </div>
                    <div>
                        <div style={{ marginBottom: 4 }}>Предполагаемая дата поставки</div>
                        <DatePicker
                            format={outputDateFormat}
                            value={estimatedDate}
                            onChange={onEstimatedDateChange}
                            disabled={readOnly}
                        />
                    </div>
                </Flex>
            </Flex>

            <Table
                rowKey='documentId'
                columns={columns}
                dataSource={draftForms}
                pagination={false}
            />
        </Space>
    )
}
